import React from 'react'
import { cloneDeep } from 'lodash'
import { Stack, Grid, Box, Divider, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useGetPriceSheetsQuery } from 'services/priceSheets'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import { ACCOUNT_TYPES } from 'utils/constants'
import { useGetSupplierTypesQuery, useGetVendorClassesQuery } from 'services/customers'
import RadioGroup from 'components/generic/form/RadioGroup'
import Checkbox from 'components/generic/form/Checkbox'
import { useFilterPriceSheets } from 'components/containers/customers/helpers'
import { sortSupplierTypeVendorClass } from 'utils/data-manipulation/common'
import { useSelector } from 'react-redux'
import { useGetGsUserListQuery } from 'services/gsUsers'

const accountTypesArray = [ACCOUNT_TYPES.CONSUMER, ACCOUNT_TYPES.SUPPLIER, ACCOUNT_TYPES.SUPPLIERANDCONSUMER]

const CustomerGeneralInfoForm = ({ getValues, control, errors, isUpdate }) => {
  const { data: priceSheetsData } = useGetPriceSheetsQuery({ filter: JSON.stringify({ type: 'PRICE_LIST' }) })
  const priceSheetList = [{ name: 'No Price List', id: null }, ...useFilterPriceSheets(priceSheetsData)]

  const { data: supplierTypesData } = useGetSupplierTypesQuery({})
  const filteredSupplierTypes = cloneDeep(supplierTypesData?.Items || [])
    .filter(item => !!item.active)
    .sort(sortSupplierTypeVendorClass)

  const { data: vendorClassesData } = useGetVendorClassesQuery({})
  const filteredVendorClasses = cloneDeep(vendorClassesData?.Items || [])
    .filter(item => !!item.active)
    .sort(sortSupplierTypeVendorClass)
  const vendorClassOptions = [{ name: 'No Vendor Class', id: null }, ...filteredVendorClasses]

  const { yardId } = useSelector(state => state.auth)
  const { data: accountManagerListData } = useGetGsUserListQuery({ yardId }, { skip: !yardId })
  const accountManagerList = [
    { name: 'No Account Manager', id: null },
    ...(accountManagerListData?.Items.map(item => ({
      ...item,
      name: `${item.firstName} ${item.lastName}`,
    })) || []),
  ]

  const isSupplier = [ACCOUNT_TYPES.SUPPLIER.id, ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id].includes(
    getValues('accountType'),
  )

  return (
    <Stack flexGrow={1} sx={{ pl: 3, pr: 4 }}>
      <Box>
        <Typography variant="h6" color="textSecondary">
          General Information
        </Typography>
        <Box py={1}>
          <Divider />
        </Box>
        <Grid container mt={1} spacing={1}>
          <Grid item md={isUpdate ? 6 : 12} xs={12}>
            <Input
              name="companyName"
              label="Full Name"
              defaultValue=""
              control={control}
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
            />
          </Grid>
          {isUpdate && (
            <Grid item md={5} xs={12} ml={2}>
              <Checkbox name="canBuy" label="Buy from this customer" control={control} />
            </Grid>
          )}
        </Grid>
        <Grid direction={isUpdate ? 'column' : 'row'} container item md={isUpdate ? 6 : 12} xs={12} mt={1} spacing={2}>
          <Grid item md={6} xs={12}>
            <Select
              name="supplierTypeId"
              label="Type"
              defaultValue=""
              control={control}
              items={filteredSupplierTypes}
              error={!!errors.supplierTypeId}
              helperText={errors.supplierTypeId?.message}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Select
              name="vendorClassId"
              label="Vendor Class"
              defaultValue=""
              control={control}
              items={vendorClassOptions}
              error={!!errors.vendorClassId}
            />
          </Grid>
        </Grid>
        <Grid direction={isUpdate ? 'column' : 'row'} container item md={isUpdate ? 6 : 12} xs={12} mt={1} spacing={2}>
          {isSupplier && (
            <Grid item md={6} xs={12}>
              <Select
                name="priceSheetId"
                label="Price List"
                defaultValue=""
                control={control}
                items={priceSheetList}
                error={!!errors.priceSheetId}
              />
            </Grid>
          )}
          <Grid item md={isSupplier ? 6 : 12} xs={12}>
            <Select
              name="accountManagerGsUserId"
              label="Account Manager"
              defaultValue=""
              control={control}
              items={accountManagerList}
              error={!!errors.accountManagerGsUserId}
            />
          </Grid>
        </Grid>
        {isUpdate && (
          <Grid container mt={1} spacing={2}>
            <Grid item md={12} xs={12}>
              <RadioGroup
                name="accountType"
                label="Supplier/Consumer"
                control={control}
                defaultValue=""
                error={!!errors.accountType}
                items={accountTypesArray}
              />
            </Grid>
          </Grid>
        )}
        <Grid container mt={1} spacing={2}>
          <Grid item md={12} xs={12}>
            <Input
              name="notes"
              label="Notes"
              defaultValue=""
              control={control}
              error={!!errors.notes}
              helperText={errors.notes?.message}
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

CustomerGeneralInfoForm.propTypes = {
  getValues: PropTypes.func,
  control: PropTypes.object,
  errors: PropTypes.object,
  isUpdate: PropTypes.bool,
}

CustomerGeneralInfoForm.defaultProps = {
  getValues: () => {},
  control: {},
  errors: {},
  isUpdate: false,
}

export default CustomerGeneralInfoForm
